import { supabase } from "../supabaseClient";
import { getOrganizationPlan } from "./organizationPlan";
import { TOURNAMENT_CAPS } from "./planTiers";

export { TOURNAMENT_CAPS };

/** Cap for the org from plan status. null = unlimited (Priority / grandfather). */
export const resolveTournamentLimit = ({
  state,
  plan,
  grandfathered,
} = {}) => {
  if (grandfathered || state === "beta") {
    return TOURNAMENT_CAPS.priority;
  }
  if (state === "subscribed" && plan === "priority") {
    return TOURNAMENT_CAPS.priority;
  }
  if (state === "subscribed" && plan === "ai_assist") {
    return TOURNAMENT_CAPS.ai_assist;
  }
  return TOURNAMENT_CAPS.free;
};

export const tournamentAtCapMessage = (tournamentLimit) => {
  const limit =
    tournamentLimit == null ? TOURNAMENT_CAPS.free : tournamentLimit;
  return `This organization has used all ${limit} tournaments for this year. Open Plan to add more.`;
};

export const tournamentCapUsageCopy = ({
  tournamentCount = 0,
  tournamentLimit,
} = {}) => {
  if (tournamentLimit == null) {
    return {
      atCap: false,
      nearCap: false,
      usage: "Unlimited tournaments per year on this plan.",
      message: "",
    };
  }

  const count = Number(tournamentCount) || 0;
  const remaining = tournamentLimit - count;
  const usage = `${count} of ${tournamentLimit} tournaments used.`;

  if (remaining <= 0) {
    return {
      atCap: true,
      nearCap: true,
      usage,
      message: tournamentAtCapMessage(tournamentLimit),
    };
  }

  if (remaining === 1) {
    return {
      atCap: false,
      nearCap: true,
      usage,
      message: "You can create 1 more tournament this year on this plan.",
    };
  }

  return {
    atCap: false,
    nearCap: false,
    usage,
    message: "",
  };
};

export const isTournamentCapError = (error) => {
  const text = `${error?.message || ""} ${error?.details || ""} ${
    error?.hint || ""
  }`;
  return /TOURNAMENT_CAP/i.test(text) || /tournament limit/i.test(text);
};

export const getOrganizationTournamentCap = async (organizationId) => {
  if (!organizationId) {
    return {
      tournamentCount: 0,
      tournamentLimit: TOURNAMENT_CAPS.free,
      canCreateTournament: true,
    };
  }

  const { data, error } = await supabase.rpc(
    "organization_tournament_cap_status",
    { p_organization_id: organizationId }
  );

  if (!error && data && typeof data === "object") {
    const tournamentCount = Number(data.tournamentCount) || 0;
    const tournamentLimit =
      data.tournamentLimit == null ? null : Number(data.tournamentLimit);
    return {
      tournamentCount,
      tournamentLimit,
      canCreateTournament:
        data.canCreateTournament == null
          ? tournamentLimit == null || tournamentCount < tournamentLimit
          : Boolean(data.canCreateTournament),
    };
  }

  const year = new Date().getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1)).toISOString();
  const nextYearStart = new Date(Date.UTC(year + 1, 0, 1)).toISOString();
  // Soft-deleted rows still count — deleting must not free a Plan slot.
  const counted = await supabase
    .from("tournaments")
    .select("id", { count: "exact", head: true })
    .eq("organizationID", organizationId)
    .gte("created", yearStart)
    .lt("created", nextYearStart);

  if (counted.error) throw counted.error;
  const tournamentCount = counted.count || 0;

  try {
    const plan = await getOrganizationPlan(organizationId);
    const tournamentLimit = resolveTournamentLimit(plan);
    return {
      tournamentCount,
      tournamentLimit,
      canCreateTournament:
        tournamentLimit == null || tournamentCount < tournamentLimit,
      capStatusUnavailable: true,
    };
  } catch (_) {
    // Fail closed to Free when the RPC is missing and Plan status is not readable.
    return {
      tournamentCount,
      tournamentLimit: TOURNAMENT_CAPS.free,
      canCreateTournament: tournamentCount < TOURNAMENT_CAPS.free,
      capStatusUnavailable: true,
    };
  }
};
